// A deep import, not the feature's barrel: the logbook's own grid reaches back
// into this module for its link controls, and going through both barrels would
// make the two features circular at module level for no benefit.
import { computeDerivedStats } from '@/features/logbook/derive-stats';
import type { AerodromeRef, Flight, LogbookEntry, Track, TrackPoints } from '@/lib/db';
import {
  flights as defaultFlightRepo,
  logbookEntries as defaultLogbookRepo,
  tracks as defaultTrackRepo,
} from '@/lib/db';
import type { FlightRepository, LogbookRepository, TrackRepository } from '@/lib/db';
import { nearestAerodrome } from '@/lib/aerodromes';
import { formatLocalDate } from '@/lib/format';

/**
 * Linking a track to a flight by hand, and breaking that link again.
 *
 * Import does this automatically (`features/logbook/draft.ts`), and automatic
 * matching is the feature — but it matches on registration and calendar day,
 * so it cannot join a paper entry transcribed years later to a track pulled
 * from FR24 last week, and it will occasionally pair the wrong two flights on
 * a busy day. Both of those need a pilot, which is what this module is for.
 *
 * Two rules hold throughout:
 *
 *  - **A track belongs to at most one flight.** Linking a track that another
 *    flight already holds moves it, rather than leaving the same evidence
 *    counted twice.
 *  - **Linking never overwrites what a pilot typed.** It recomputes
 *    `Flight.derived` — that field is explicitly the app's own suggestion —
 *    and fills a blank departure or arrival, and stops there. Logbook hours
 *    stay exactly as they are; the entry is the record, and the grid is where
 *    it gets edited.
 */

export interface LinkDeps {
  flightRepo?: FlightRepository;
  logbookRepo?: LogbookRepository;
  trackRepo?: TrackRepository;
  now?: () => string;
}

export interface LinkResult {
  flight: Flight;
  /** Flights the track was taken off, if it was already linked elsewhere. */
  detachedFrom: Flight[];
}

/** The first fix that is actually a position — a leading NaN is a gap, not a departure point. */
function firstFinite(
  points: TrackPoints,
  fromEnd = false,
): { lat: number; lon: number } | undefined {
  const count = points.lat.length;
  for (let step = 0; step < count; step += 1) {
    const i = fromEnd ? count - 1 - step : step;
    const lat = points.lat[i];
    const lon = points.lon[i];
    if (lat !== undefined && lon !== undefined && Number.isFinite(lat) && Number.isFinite(lon)) {
      return { lat, lon };
    }
  }
  return undefined;
}

function aerodromeAt(lat: number | undefined, lon: number | undefined): AerodromeRef | undefined {
  if (lat === undefined || lon === undefined || !Number.isFinite(lat) || !Number.isFinite(lon)) {
    return undefined;
  }
  const match = nearestAerodrome(lat, lon);
  if (match === undefined) {
    return undefined;
  }
  const { icao, name, lat: aLat, lon: aLon } = match.aerodrome;
  return {
    ...(icao === undefined ? {} : { icao }),
    ...(name === undefined ? {} : { name }),
    ...(aLat === undefined ? {} : { lat: aLat }),
    ...(aLon === undefined ? {} : { lon: aLon }),
  };
}

/**
 * Points a flight at a track.
 *
 * @throws if either record is missing — a link to something that isn't there
 *   is a bug in the caller, not a state worth persisting.
 */
export async function linkTrackToFlight(
  flightId: string,
  trackId: string,
  deps: LinkDeps = {},
): Promise<LinkResult> {
  const flightRepo = deps.flightRepo ?? defaultFlightRepo;
  const trackRepo = deps.trackRepo ?? defaultTrackRepo;
  const now = deps.now ?? (() => new Date().toISOString());

  const flight = await flightRepo.get(flightId);
  if (flight === undefined) {
    throw new Error(`No flight ${flightId} to link a track to.`);
  }
  const track = await trackRepo.get(trackId);
  if (track === undefined) {
    throw new Error(`No track ${trackId} to link.`);
  }

  const detachedFrom: Flight[] = [];
  for (const other of await flightRepo.listByTrack(trackId)) {
    if (other.id === flightId) {
      continue;
    }
    // `delete` rather than `trackId: undefined`: the app compiles with
    // `exactOptionalPropertyTypes`, and an absent link and a link explicitly
    // set to `undefined` are not the same value to Dexie's index either.
    const detached: Flight = { ...other, derived: { confidence: 'low' }, updatedAt: now() };
    delete detached.trackId;
    await flightRepo.put(detached);
    detachedFrom.push(detached);
  }

  const points = await trackRepo.getPoints(trackId);
  const derived = points === undefined ? flight.derived : computeDerivedStats(track, points);

  const start = points === undefined ? undefined : firstFinite(points);
  const end = points === undefined ? undefined : firstFinite(points, true);
  const departure = flight.departure ?? aerodromeAt(start?.lat, start?.lon);
  const arrival = flight.arrival ?? aerodromeAt(end?.lat, end?.lon);

  const linked: Flight = {
    ...flight,
    trackId,
    ...(departure === undefined ? {} : { departure }),
    ...(arrival === undefined ? {} : { arrival }),
    derived,
    updatedAt: now(),
  };
  await flightRepo.put(linked);

  return { flight: linked, detachedFrom };
}

/**
 * Breaks the link, keeping every number the entry already carries.
 *
 * What does change is the "from the track" marking on a draft entry: with no
 * track behind it, a field claiming to come from one is a lie the grid would
 * keep telling. The values stay and become the pilot's own, which is what
 * unlinking a wrongly-matched track should leave behind.
 */
export async function unlinkTrackFromFlight(
  flightId: string,
  deps: LinkDeps = {},
): Promise<Flight | undefined> {
  const flightRepo = deps.flightRepo ?? defaultFlightRepo;
  const logbookRepo = deps.logbookRepo ?? defaultLogbookRepo;
  const now = deps.now ?? (() => new Date().toISOString());

  const flight = await flightRepo.get(flightId);
  if (flight?.trackId === undefined) {
    return flight;
  }

  const timestamp = now();
  const unlinked: Flight = { ...flight, derived: { confidence: 'low' }, updatedAt: timestamp };
  delete unlinked.trackId;
  await flightRepo.put(unlinked);

  const entry = await logbookRepo.findByFlight(flightId);
  if (entry !== undefined && entry.derivedFields.length > 0) {
    const cleared: LogbookEntry = { ...entry, derivedFields: [], updatedAt: timestamp };
    await logbookRepo.put(cleared);
  }

  return unlinked;
}

export interface TrackCandidate {
  track: Track;
  /** Why it is being offered, in the order the list is ranked by. */
  reason: 'same-aircraft-same-day' | 'same-aircraft' | 'same-day' | 'unlinked';
  /** Whole days between the track's start and the flight's date. */
  dayGap: number;
}

const REASON_RANK: Record<TrackCandidate['reason'], number> = {
  'same-aircraft-same-day': 0,
  'same-aircraft': 1,
  'same-day': 2,
  unlinked: 3,
};

/*
 * Whole days between a stored calendar date and a track's start.
 *
 * The track's day is the *local* one, not the UTC slice of its timestamp: a
 * flight that left Ardmore at 10:31 NZDT is stored as 21:31Z the day before,
 * and ranking it a day away from the entry a pilot dated 14 March would push
 * the obvious match down the list on every afternoon flight in New Zealand.
 */
function dayGapBetween(date: string, startTime: string): number {
  const flightDay = Date.parse(`${date}T00:00:00Z`);
  const trackDay = Date.parse(`${formatLocalDate(startTime, { format: 'iso' })}T00:00:00Z`);
  if (Number.isNaN(flightDay) || Number.isNaN(trackDay)) {
    return Number.MAX_SAFE_INTEGER;
  }
  return Math.round(Math.abs(trackDay - flightDay) / 86_400_000);
}

/**
 * Tracks a pilot could plausibly mean, best first.
 *
 * Only tracks no flight already holds are offered: moving a track off another
 * flight is supported, but it is not something to suggest. Ranking is the
 * obvious one — same aircraft and same day, then same aircraft, then same day,
 * then everything else — and within a rank, closest in time.
 *
 * @param registration  the flight's aircraft, if known. Nothing is filtered out
 *   when it isn't; a track that doesn't say which aeroplane it was is exactly
 *   the case that needs a human.
 */
export async function suggestTracksForFlight(
  flight: Pick<Flight, 'date'>,
  registration: string | undefined,
  deps: LinkDeps & { limit?: number } = {},
): Promise<TrackCandidate[]> {
  const flightRepo = deps.flightRepo ?? defaultFlightRepo;
  const trackRepo = deps.trackRepo ?? defaultTrackRepo;
  const limit = deps.limit ?? 20;

  const allTracks = await trackRepo.listRecent(500);
  const linkedTrackIds = new Set(
    (await flightRepo.listRecent(500))
      .map((row) => row.trackId)
      .filter((id): id is string => id !== undefined),
  );

  const candidates: TrackCandidate[] = [];
  for (const track of allTracks) {
    if (linkedTrackIds.has(track.id)) {
      continue;
    }
    const dayGap = dayGapBetween(flight.date, track.startTime);
    const sameAircraft = registration !== undefined && track.registration === registration;
    const sameDay = dayGap === 0;
    const reason: TrackCandidate['reason'] = sameAircraft
      ? sameDay
        ? 'same-aircraft-same-day'
        : 'same-aircraft'
      : sameDay
        ? 'same-day'
        : 'unlinked';
    candidates.push({ track, reason, dayGap });
  }

  candidates.sort((a, b) => {
    const rank = REASON_RANK[a.reason] - REASON_RANK[b.reason];
    if (rank !== 0) {
      return rank;
    }
    if (a.dayGap !== b.dayGap) {
      return a.dayGap - b.dayGap;
    }
    return b.track.startTime.localeCompare(a.track.startTime);
  });

  return candidates.slice(0, limit);
}

export const CANDIDATE_REASON_LABEL: Record<TrackCandidate['reason'], string> = {
  'same-aircraft-same-day': 'Same aircraft, same day',
  'same-aircraft': 'Same aircraft',
  'same-day': 'Same day',
  unlinked: 'Not linked to a flight',
};

export interface EntryCandidate {
  entry: LogbookEntry;
  flight: Flight;
  reason: TrackCandidate['reason'];
  dayGap: number;
}

/**
 * Logbook entries this track could belong to, best first — the mirror of
 * `suggestTracksForFlight`, for the pilot standing on a flight's page looking
 * for the paper entry it matches.
 *
 * Only entries whose flight has no track are offered, for the same reason:
 * one track, one flight.
 */
export async function suggestEntriesForTrack(
  track: Pick<Track, 'startTime' | 'registration'>,
  deps: LinkDeps & { limit?: number } = {},
): Promise<EntryCandidate[]> {
  const flightRepo = deps.flightRepo ?? defaultFlightRepo;
  const logbookRepo = deps.logbookRepo ?? defaultLogbookRepo;
  const limit = deps.limit ?? 20;

  const entries = await logbookRepo.listOrdered();
  const candidates: EntryCandidate[] = [];

  for (const entry of entries) {
    const flight = await flightRepo.get(entry.flightId);
    if (flight === undefined || flight.trackId !== undefined) {
      continue;
    }
    const dayGap = dayGapBetween(entry.date, track.startTime);
    const sameAircraft =
      track.registration !== undefined && entry.registration === track.registration;
    const sameDay = dayGap === 0;
    const reason: TrackCandidate['reason'] = sameAircraft
      ? sameDay
        ? 'same-aircraft-same-day'
        : 'same-aircraft'
      : sameDay
        ? 'same-day'
        : 'unlinked';
    candidates.push({ entry, flight, reason, dayGap });
  }

  candidates.sort((a, b) => {
    const rank = REASON_RANK[a.reason] - REASON_RANK[b.reason];
    if (rank !== 0) {
      return rank;
    }
    if (a.dayGap !== b.dayGap) {
      return a.dayGap - b.dayGap;
    }
    return b.entry.date.localeCompare(a.entry.date);
  });

  return candidates.slice(0, limit);
}
